/**
 * PriorityDragger Component
 *
 * Drag-and-drop interface for managing provider pool priorities.
 * Uses @dnd-kit for accessible, performant drag operations.
 */

import React, { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clsx } from 'clsx';

export interface PriorityItem {
  id: string;
  providerName: string;
  poolIndex: number;
  label: string;
}

export interface PriorityDraggerProps {
  items: PriorityItem[];
  onChange: (items: PriorityItem[]) => void;
  disabled?: boolean;
  className?: string;
}

interface SortableItemProps {
  item: PriorityItem;
  disabled: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  disabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg',
        isDragging && 'opacity-50 shadow-lg',
        !disabled && 'cursor-grab active:cursor-grabbing'
      )}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center gap-2 flex-1">
        <span className="text-gray-400">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800">
              {item.label}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {item.providerName} / 池 #{item.poolIndex}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PriorityDragger: React.FC<PriorityDraggerProps> = ({
  items,
  onChange,
  disabled = false,
  className = '',
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        onChange(arrayMove(items, oldIndex, newIndex));
      }
    },
    [items, onChange]
  );

  const activeItem = activeId
    ? items.find((item) => item.id === activeId)
    : null;

  return (
    <div className={className}>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">池优先级顺序</h4>
        <span className="text-xs text-gray-500">
          拖拽调整顺序，顶部优先级最高
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={item.id} className="flex items-center gap-2">
                <span className="w-6 text-center text-sm font-medium text-gray-400">
                  {index + 1}
                </span>
                <SortableItem
                  item={item}
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeItem ? (
            <div className="flex items-center gap-2 p-3 bg-white border border-blue-300 rounded-lg shadow-lg">
              <span className="text-sm font-medium text-gray-800">
                {activeItem.label}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {items.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">
          暂无池配置
        </div>
      )}

      <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
        <strong>提示：</strong>拖拽列表项可调整优先级顺序。
      </div>
    </div>
  );
};

export default PriorityDragger;
